import { useCallback, useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import StatusPill from "@/components/StatusPill";
import api from "@/lib/api";
import twilioDevice from "@/lib/twilioDevice";
import CallDetailModal from "@/components/CallDetailModal";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatTime } from "@/lib/datetime";
import { MagnifyingGlass, ArrowClockwise, Download, Play, Phone, Robot, ArrowDownLeft, ArrowUpRight, ArrowsLeftRight, ArrowSquareOut, CaretLeft, CaretRight, Warning, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

function fmtDur(s) {
    if (!s) return "0:00";
    const m = Math.floor(s / 60);
    const sec = String(s % 60).padStart(2, "0");
    return `${m}:${sec}`;
}

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const sentDotClass = (s) => {
    const v = (s || "").toLowerCase();
    return v === "positive" ? "bg-emerald-500" : v === "negative" ? "bg-red-500" : "bg-stone-300";
};

const PAGE_SIZE = 50;
const FILTERS = [
    { key: "all", label: "All" },
    { key: "completed", label: "Completed" },
    { key: "missed", label: "Missed" },
    { key: "failed", label: "Failed" },
    { key: "live", label: "Live" },
];
const RANGES = [
    { key: "today", label: "Today" },
    { key: "week", label: "This Week" },
    { key: "month", label: "Last 30 days" },
    { key: "all", label: "All" },
];

// Start of the selected range as a UTC ISO string (null = no lower bound).
const rangeStartISO = (r) => {
    const now = new Date();
    const d = new Date(now);
    if (r === "today") { d.setHours(0, 0, 0, 0); return d.toISOString(); }
    if (r === "week") { d.setDate(d.getDate() - 7); return d.toISOString(); }
    if (r === "month") { d.setDate(d.getDate() - 30); return d.toISOString(); }
    return null;
};

export default function CallHistory() {
    const { orgTimezone } = useAuth();
    const [calls, setCalls] = useState([]);
    const [counts, setCounts] = useState({ all: 0, completed: 0, missed: 0, failed: 0, live: 0 });
    const [q, setQ] = useState("");
    const [debouncedQ, setDebouncedQ] = useState("");
    const [range, setRange] = useState("today");
    const [statusFilter, setStatusFilter] = useState("all");
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [detail, setDetail] = useState(null);
    const [sf, setSf] = useState({ connected: false, instanceUrl: null });

    const sfLinkFor = (c) => {
        if (!sf.connected || !sf.instanceUrl) return null;
        const rid = c.sf_task_id || c.sf_who_id;
        return rid ? `${sf.instanceUrl.replace(/\/$/, "")}/${rid}` : null;
    };

    // Debounce the search box (server-side search runs against the whole range).
    useEffect(() => {
        const t = setTimeout(() => { setDebouncedQ(q.trim()); setPage(0); }, 400);
        return () => clearTimeout(t);
    }, [q]);

    const loadCounts = useCallback(async () => {
        try {
            const params = {};
            const start = rangeStartISO(range);
            if (start) params.start = start;
            if (debouncedQ) params.q = debouncedQ;
            const { data } = await api.get("/calls/count", { params });
            setCounts(data.buckets || { all: 0 });
        } catch (_e) { /* non-fatal */ }
    }, [range, debouncedQ]);

    const loadList = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
            const start = rangeStartISO(range);
            if (start) params.start = start;
            if (statusFilter !== "all") params.status = statusFilter;
            if (debouncedQ) params.q = debouncedQ;
            const { data } = await api.get("/calls", { params });
            setCalls(data);
        } finally {
            setLoading(false);
        }
    }, [range, statusFilter, debouncedQ, page]);

    useEffect(() => { loadCounts(); }, [loadCounts]);
    useEffect(() => { loadList(); }, [loadList]);

    // One-time: reconcile any live/pending provider states in the background, then refresh.
    useEffect(() => {
        (async () => {
            try {
                await api.post("/calls/reconcile");
                await loadList();
                await loadCounts();
            } catch (_e) { /* non-fatal */ }
        })();
        (async () => {
            try {
                const { data } = await api.get("/integrations/salesforce");
                setSf({ connected: !!data.connected, instanceUrl: data.instance_url || null });
            } catch (_e) { /* SF not configured — hide the link */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refresh = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try { await api.post("/calls/reconcile"); } catch (_e) { /* non-fatal */ }
        try { await loadList(); await loadCounts(); } finally { setRefreshing(false); }
    };

    const total = counts[statusFilter] ?? 0;
    const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
    const to = Math.min((page + 1) * PAGE_SIZE, total);
    const hasNext = (page + 1) * PAGE_SIZE < total;
    const colCount = sf.connected ? 9 : 8;

    return (
        <AppShell>
            <div className="pb-2">
                {/* Sticky page header */}
                <div className="sticky top-0 z-40 border-b border-stone-200 bg-paper/85 backdrop-blur-xl">
                    <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 px-6 py-5 md:flex-row md:items-center md:px-12">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone2">History</div>
                            <h1 className="mt-1 font-heading text-2xl font-bold tracking-tight sm:text-3xl">Call History</h1>
                            <p className="mt-1 text-sm text-stone2">Every call logged across your workspace.</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={refresh}
                                disabled={refreshing}
                                data-testid="refresh-button"
                                className="inline-flex h-9 items-center gap-2 rounded-full border border-stone-300 bg-white px-4 text-sm font-semibold text-ink shadow-sm transition-colors hover:bg-stone-50 disabled:opacity-60"
                            >
                                <ArrowClockwise size={16} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Syncing…" : "Refresh"}
                            </button>
                            <button
                                onClick={async () => {
                                    try {
                                        const { data } = await api.post("/calls/refresh-recordings");
                                        if (data.refreshed > 0) toast.success(`Fetched ${data.refreshed} recording${data.refreshed > 1 ? "s" : ""}`);
                                        else toast.info("No new recordings found");
                                        await loadList();
                                    } catch (_e) { toast.error("Failed to refresh recordings"); }
                                }}
                                data-testid="refresh-recordings-button"
                                className="inline-flex h-9 items-center gap-2 rounded-full bg-ink px-4 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5"
                            >
                                <Download size={16} /> Sync recordings
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mx-auto max-w-7xl px-6 py-6 md:px-12">

                {/* Toolbar: period + search */}
                <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="inline-flex flex-wrap items-center gap-1 rounded-xl border border-stone-200 bg-stone-100 p-1" data-testid="range-chips">
                        {RANGES.map((r) => (
                            <button
                                key={r.key}
                                data-testid={`range-${r.key}`}
                                onClick={() => { setRange(r.key); setPage(0); }}
                                className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                                    range === r.key ? "bg-white text-ink shadow-sm" : "text-stone2 hover:text-ink"
                                }`}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                    <div className="relative lg:w-80">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                        <input
                            data-testid="search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search number, agent, notes…"
                            className="h-9 w-full rounded-full border border-stone-300 bg-white pl-9 pr-4 text-sm outline-none focus:border-ink focus:ring-2 focus:ring-brand/30"
                        />
                    </div>
                </div>

                {/* Status filters */}
                <div className="mb-4 inline-flex flex-wrap items-center gap-1 rounded-xl border border-stone-200 bg-stone-100 p-1">
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            data-testid={`filter-${f.key}`}
                            onClick={() => { setStatusFilter(f.key); setPage(0); }}
                            className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                                statusFilter === f.key ? "bg-white text-ink shadow-sm" : "text-stone2 hover:text-ink"
                            }`}
                        >
                            {f.label}
                            <span className={`tabular ${statusFilter === f.key ? "text-stone-400" : "text-stone-400"}`}>{counts[f.key] ?? 0}</span>
                        </button>
                    ))}
                </div>

                <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white">
                    <table className="w-full min-w-[760px] text-left">
                        <thead>
                            <tr className="border-b border-stone-200 bg-stone-50/70">
                                {["When", "Agent", "From", "To", "Status", ...(sf.connected ? ["SF"] : []), "Duration", "Rec", "Notes"].map((h) => (
                                    <th key={h} className="whitespace-nowrap px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={colCount} className="px-4 py-12 text-center text-sm text-stone2">Loading…</td>
                                </tr>
                            )}
                            {!loading && calls.length === 0 && (
                                <tr>
                                    <td colSpan={colCount} className="px-4 py-12 text-center text-sm text-stone2" data-testid="history-empty">
                                        No calls in this period. Try a wider period or clear your search.
                                    </td>
                                </tr>
                            )}
                            {!loading && calls.map((c) => {
                                const inbound = c.direction === "inbound";
                                return (
                                <tr key={c.id} className="group border-t border-stone-100 hover:bg-stone-50" data-testid={`call-row-${c.id}`}>
                                    <td className="whitespace-nowrap px-4 py-3 align-top">
                                        <div className="text-sm font-medium text-ink">{formatDate(c.created_at, orgTimezone)}</div>
                                        <div className="tabular text-xs text-stone2">{formatTime(c.created_at, orgTimezone)}</div>
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                        <div className="max-w-[130px] truncate text-sm font-medium" title={c.user_name || ""}>
                                            {c.user_name || "—"}
                                        </div>
                                        {c.transferred_from && (
                                            <span
                                                data-testid={`transfer-badge-${c.id}`}
                                                title={`Transferred from ${c.transferred_from}${c.transferred_to ? ` to ${c.transferred_to}` : ""}`}
                                                className="mt-1 flex w-fit max-w-[130px] items-center gap-1 truncate rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700"
                                            >
                                                <ArrowsLeftRight size={11} weight="bold" className="shrink-0" />
                                                <span className="truncate">{c.transferred_from} → {c.transferred_to || "—"}</span>
                                            </span>
                                        )}
                                    </td>
                                    <td className="whitespace-nowrap px-4 py-3 tabular text-sm align-top" data-testid={`from-${c.id}`}>
                                        {inbound ? (
                                            <button
                                                data-testid={`redial-${c.id}`}
                                                onClick={() => twilioDevice.requestDial(c.from_number)}
                                                disabled={!c.from_number}
                                                className="inline-flex items-center gap-1.5 font-heading tabular text-sm font-medium hover:text-brand"
                                                title="Click to call back"
                                            >
                                                <ArrowDownLeft size={13} weight="bold" className="text-emerald-500" />
                                                {c.from_number || "—"}
                                            </button>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 text-stone2">
                                                <ArrowUpRight size={13} weight="bold" className="text-stone-400" />
                                                {c.from_number || "—"}
                                            </span>
                                        )}
                                    </td>
                                    <td className="whitespace-nowrap px-4 py-3 tabular text-sm align-top" data-testid={`to-${c.id}`}>
                                        {!inbound ? (
                                            <button
                                                data-testid={`redial-${c.id}`}
                                                onClick={() => twilioDevice.requestDial(c.to_number)}
                                                disabled={!c.to_number}
                                                className="inline-flex items-center gap-1.5 font-heading tabular text-sm font-medium hover:text-brand"
                                                title="Click to redial"
                                            >
                                                <Phone size={12} weight="fill" className="opacity-0 transition-opacity group-hover:opacity-100" />
                                                {c.to_number || "—"}
                                            </button>
                                        ) : (
                                            <span className="text-stone2">{c.to_number || "—"}</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                        <div className="flex items-center gap-2">
                                            <StatusPill status={c.status} />
                                            {c.ai_analysis?.sentiment && (
                                                <span
                                                    data-testid={`sentiment-dot-${c.id}`}
                                                    title={`Sentiment: ${cap(c.ai_analysis.sentiment)}`}
                                                    className={`h-2.5 w-2.5 shrink-0 rounded-full ${sentDotClass(c.ai_analysis.sentiment)}`}
                                                />
                                            )}
                                        </div>
                                    </td>
                                    {sf.connected && (
                                        <td className="px-4 py-3 align-top">
                                            {sfLinkFor(c) ? (
                                                <a
                                                    href={sfLinkFor(c)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`sf-open-${c.id}`}
                                                    title={c.sf_task_id ? "Open the logged Task in Salesforce" : "Open the matched contact in Salesforce"}
                                                    className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-1 text-xs font-semibold text-sky-800 transition-colors hover:bg-sky-200"
                                                >
                                                    <ArrowSquareOut size={12} weight="bold" /> Open
                                                </a>
                                            ) : (
                                                <span className="text-xs text-stone-400">—</span>
                                            )}
                                        </td>
                                    )}
                                    <td className="whitespace-nowrap px-4 py-3 tabular text-sm align-top">{fmtDur(c.duration_sec)}</td>
                                    <td className="px-4 py-3 align-top">
                                        {c.handled_by_ai ? (
                                            <button
                                                onClick={() => setDetail(c)}
                                                data-testid={`recording-${c.id}`}
                                                className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-1 text-xs font-semibold text-violet-800 transition-colors hover:bg-violet-200"
                                            >
                                                <Robot size={12} weight="fill" /> Transcript
                                            </button>
                                        ) : c.recording_url ? (
                                            <button
                                                onClick={() => setDetail(c)}
                                                data-testid={`recording-${c.id}`}
                                                className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800 transition-colors hover:bg-emerald-200"
                                            >
                                                <Play size={12} weight="fill" /> Play
                                            </button>
                                        ) : c.recording_health === "unavailable" ? (
                                            <span
                                                data-testid={`recording-unavailable-${c.id}`}
                                                title="This call was recorded but the recording never became downloadable. It may have failed to upload from the provider."
                                                className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800"
                                            >
                                                <Warning size={12} weight="fill" /> No recording
                                            </span>
                                        ) : c.recording_health === "pending" ? (
                                            <span
                                                data-testid={`recording-pending-${c.id}`}
                                                title="The recording is still being prepared by the provider — check back shortly."
                                                className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-1 text-xs font-medium text-stone-500"
                                            >
                                                <Clock size={12} /> Processing
                                            </span>
                                        ) : (
                                            <span className="text-xs text-stone-400">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                        {c.notes ? (
                                            <button
                                                type="button"
                                                onClick={() => setDetail(c)}
                                                data-testid={`notes-${c.id}`}
                                                title="Click to view / edit the full note"
                                                className="line-clamp-2 max-w-[280px] text-left text-sm text-stone2 transition-colors hover:text-ink hover:underline decoration-dotted underline-offset-2"
                                            >
                                                {c.notes}
                                            </button>
                                        ) : (
                                            <span className="text-sm text-stone-400">—</span>
                                        )}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
                    <div className="text-xs text-stone2" data-testid="pagination-summary">
                        {total === 0 ? "No calls" : <>Showing <b>{from}–{to}</b> of <b>{total}</b></>}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="page-prev"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0 || loading}
                            className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold text-stone2 transition-all hover:border-ink disabled:opacity-40"
                        >
                            <CaretLeft size={13} weight="bold" /> Prev
                        </button>
                        <span className="tabular text-xs text-stone-400">Page {page + 1}</span>
                        <button
                            data-testid="page-next"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={!hasNext || loading}
                            className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold text-stone2 transition-all hover:border-ink disabled:opacity-40"
                        >
                            Next <CaretRight size={13} weight="bold" />
                        </button>
                    </div>
                </div>
            </div>
            </div>

            {detail && (
                <CallDetailModal
                    call={detail}
                    sfLink={sfLinkFor(detail)}
                    onClose={() => setDetail(null)}
                    onSaved={() => loadList()}
                />
            )}
        </AppShell>
    );
}
